import { hash01, distToLine2 } from "./trailGeometry";

/**
 * cobbleStones — the pure stone fill for the cobblestone path.
 *
 * Split from CobbleTrail (the renderer) so the placement logic can be unit-tested
 * without React/DOM. Given the shared geometry (centerline + segment index), it
 * lays a jittered lattice over the path's bounding box, turns each cell into an
 * irregular quad inset toward its centroid (the mortar gap), and keeps a stone
 * ONLY if all of it lies within (W/2 - margin) of the centerline — stones crossing
 * an edge or the start/end cap are dropped, leaving a clean mortar margin instead
 * of a cut stone. Corners, side edges and ragged lengths fall out of that one test.
 */

// ---- tunables --------------------------------------------------------------
export const TONE_CLASSES = 4; // stone tone variants (styled in CobbleTrail.css)
const STONES_ACROSS = 7; // ~lattice cells across the path width (cell = W / this);
//                          ~5 whole stones survive across once edge ones are dropped
const MORTAR_FRAC = 0.17; // stone shrink toward its centroid -> the mortar gap
const JITTER_FRAC = 0.34; // lattice-corner jitter, as a fraction of a cell
const EDGE_MARGIN_FRAC = -0.1; // keep stones this far inside the path edge (×W);
//                            0 = stones fill right to the edge (no inward trim).
//                            Was 0.1 — restore that for a clean mortar-margin look.

// Lay a jittered lattice over the path's bounding box; turn each cell into an
// irregular quad inset toward its centroid (the mortar gap); keep only stones that
// sit fully within (W/2 - margin) of the centerline. Returns one concatenated path
// string per tone (every same-tone stone is a subpath of a single <path>, so the
// whole field is just TONE_CLASSES nodes no matter how dense it gets).
export function buildStones({ line, index, W, width, minY, maxY }) {
  const cell = W / STONES_ACROSS;
  const jit = cell * JITTER_FRAC;
  const inner = W / 2 - W * EDGE_MARGIN_FRAC; // keep-zone radius
  const inner2 = inner * inner;
  // A cell whose UN-jittered centre is farther than this can't yield a whole stone
  // (its inset corners sit within ~cell+jit of the centre) — reject it before any
  // hashing, which skips the ~80% of cells off the thin ribbon for almost nothing.
  const far = inner + cell + jit;
  const far2 = far * far;

  // Jittered lattice node positions, computed inline from the positional hash (no
  // cache): neighbouring cells share corners because the hash is keyed on (ix, iy).
  const nodeX = (ix, iy) => ix * cell + (hash01(ix, iy, 1) * 2 - 1) * jit;
  const nodeY = (ix, iy) => minY + iy * cell + (hash01(ix, iy, 2) * 2 - 1) * jit;
  const k = 1 - MORTAR_FRAC; // inset factor toward the centroid

  const cols = Math.ceil(width / cell) + 1;
  const lrows = Math.ceil((maxY - minY) / cell) + 1;
  const tonePaths = new Array(TONE_CLASSES).fill("");
  for (let ix = 0; ix < cols; ix++) {
    for (let iy = 0; iy < lrows; iy++) {
      // Reject far-off cells by their nominal (un-jittered) centre, before hashing.
      const ncx = (ix + 0.5) * cell;
      const ncy = minY + (iy + 0.5) * cell;
      if (distToLine2(ncx, ncy, line, index) > far2) continue;
      // Jittered corners -> centroid -> inset toward the centroid (the mortar gap).
      const x0 = nodeX(ix, iy);
      const y0 = nodeY(ix, iy);
      const x1 = nodeX(ix + 1, iy);
      const y1 = nodeY(ix + 1, iy);
      const x2 = nodeX(ix + 1, iy + 1);
      const y2 = nodeY(ix + 1, iy + 1);
      const x3 = nodeX(ix, iy + 1);
      const y3 = nodeY(ix, iy + 1);
      const cx = (x0 + x1 + x2 + x3) / 4;
      const cy = (y0 + y1 + y2 + y3) / 4;
      const sx0 = cx + (x0 - cx) * k;
      const sy0 = cy + (y0 - cy) * k;
      const sx1 = cx + (x1 - cx) * k;
      const sy1 = cy + (y1 - cy) * k;
      const sx2 = cx + (x2 - cx) * k;
      const sy2 = cy + (y2 - cy) * k;
      const sx3 = cx + (x3 - cx) * k;
      const sy3 = cy + (y3 - cy) * k;
      // Keep only if every inset corner is well inside the path edge (no cut stones).
      if (distToLine2(sx0, sy0, line, index) > inner2) continue;
      if (distToLine2(sx1, sy1, line, index) > inner2) continue;
      if (distToLine2(sx2, sy2, line, index) > inner2) continue;
      if (distToLine2(sx3, sy3, line, index) > inner2) continue;
      const tone = (hash01(ix, iy, 3) * TONE_CLASSES) | 0;
      tonePaths[tone] +=
        `M${sx0.toFixed(1)},${sy0.toFixed(1)}` +
        `L${sx1.toFixed(1)},${sy1.toFixed(1)}` +
        `L${sx2.toFixed(1)},${sy2.toFixed(1)}` +
        `L${sx3.toFixed(1)},${sy3.toFixed(1)}Z`;
    }
  }
  return tonePaths;
}
